// src/views/adminView/contractsView/compliance/ComplianceShared.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Piezas visuales compartidas del sistema de cumplimiento documental:
// badges/pills de estado, barras de carpeta y crédito, logo de empresa,
// chip para listas y toasts. Todo con tokens del design system MU.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileClock,
  FileWarning,
  FileX2,
  Minus,
  ShieldAlert,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { formatCLP, formatDate } from '../../../../utils/formatters';
import { Badge } from '../../../../components/ui/Badge';
import {
  diasHasta,
  motivosDe,
  resumenDeContract,
  ESTADO_CUMPLIMIENTO_CORTO,
  ESTADO_CUMPLIMIENTO_LABEL,
  ESTADO_CUMPLIMIENTO_TONE,
} from './complianceUtils';
import { readComplianceStored } from './useComplianceStore';

// Icono lucide por estado agregado de cumplimiento (vive aquí y no en
// complianceUtils.js para mantener los utils sin imports de React/lucide).
export const ICONO_ESTADO = {
  al_dia: CheckCircle2,
  por_vencer: Clock,
  incompleto: FileWarning,
  bloqueado: ShieldAlert,
};

/** Badge del estado agregado de cumplimiento (corto para tablas/chips). */
export function EstadoCumplimientoBadge({ estado, corto, className }) {
  const { t } = useTranslation();
  const Icon = ICONO_ESTADO[estado] ?? Clock;
  const labelLargo = t(ESTADO_CUMPLIMIENTO_LABEL[estado] ?? estado);
  const label = corto ? t(ESTADO_CUMPLIMIENTO_CORTO[estado] ?? estado) : labelLargo;
  return (
    <Badge
      tone={ESTADO_CUMPLIMIENTO_TONE[estado] ?? 'neutral'}
      className={className}
      title={labelLargo}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </Badge>
  );
}

/** Barra segmentada de la carpeta documental: X de Y obligatorios conformes. */
export function CumplimientoBar({ conformes, total, className }) {
  const completo = total > 0 && conformes >= total;
  return (
    <div className={cn('flex min-w-[104px] items-center gap-2', className)}>
      <div className="flex flex-1 gap-0.5" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-2 flex-1 rounded-sm',
              i < conformes ? (completo ? 'bg-status-success' : 'bg-brand-500') : 'bg-bg-muted',
            )}
          />
        ))}
      </div>
      <span
        className={cn(
          'text-[11px] tabular-nums',
          completo ? 'text-status-success' : 'text-fg-muted',
        )}
      >
        {conformes}/{total}
      </span>
    </div>
  );
}

/** Barra continua de utilización del cupo de crédito. */
export function CreditoBar({ limite, utilizado, detalle, className }) {
  const { t } = useTranslation();
  const pct = limite > 0 ? Math.min(100, Math.round((utilizado / limite) * 100)) : 0;
  const barColor =
    pct >= 90 ? 'bg-status-error' : pct >= 70 ? 'bg-status-warning' : 'bg-brand-500';
  return (
    <div className={cn('min-w-[120px]', className)}>
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-muted" aria-hidden="true">
          <div className={cn('h-full rounded-full', barColor)} style={{ width: `${pct}%` }} />
        </div>
        <span
          className={cn(
            'text-[11px] tabular-nums',
            pct >= 90 ? 'font-medium text-status-error' : 'text-fg-muted',
          )}
        >
          {pct}%
        </span>
      </div>
      {detalle && (
        <div className="mt-0.5 text-[11px] text-fg-muted">
          {t('{{utilizado}} de {{limite}}', {
            utilizado: formatCLP(utilizado),
            limite: formatCLP(limite),
          })}
        </div>
      )}
    </div>
  );
}

const PILL_ICONS = {
  vigente: CheckCircle2,
  por_vencer: Clock,
  vencido: AlertTriangle,
  pendiente: Minus,
  en_revision: FileClock,
  rechazado: FileX2,
};

const PILL_STYLES = {
  vigente: 'bg-status-success-soft text-status-success',
  por_vencer: 'bg-status-warning-soft text-status-warning',
  vencido: 'bg-status-error-soft text-status-error',
  pendiente: 'bg-bg-muted text-fg-muted',
  en_revision: 'bg-status-info-soft text-status-info',
  rechazado: 'bg-status-error-soft text-status-error',
};

/** Pill de vencimiento/estado de un documento individual (EstadoDoc). */
export function VencimientoPill({ venceAt, estado, compacto, className }) {
  const { t } = useTranslation();
  const Icon = PILL_ICONS[estado] ?? Minus;
  let texto;
  if (estado === 'pendiente') {
    texto = t('Sin cargar');
  } else if (estado === 'en_revision') {
    texto = t('En revisión');
  } else if (estado === 'rechazado') {
    texto = t('Rechazado');
  } else if (!venceAt) {
    texto = t('No vence');
  } else {
    const d = diasHasta(venceAt);
    if (d < 0) texto = t('Vencido hace {{d}} d', { d: Math.abs(d) });
    else if (d === 0) texto = t('Vence hoy');
    else if (d <= 30) texto = t('Vence en {{d}} d', { d });
    else if (compacto) texto = t('Vigente');
    else texto = t('Vigente hasta {{fecha}}', { fecha: formatDate(venceAt) });
  }
  const title = venceAt
    ? t('Vence el {{fecha}}', { fecha: formatDate(venceAt) })
    : t('Documento sin fecha de vencimiento');
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]',
        PILL_STYLES[estado] ?? PILL_STYLES.pendiente,
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {texto}
    </span>
  );
}

/**
 * Iniciales (máx 2) del nombre de la empresa para el primitivo Avatar —
 * mismo criterio que EmpresaCard y el detalle del hub. (El antiguo
 * LogoEmpresa duplicaba Avatar con un color hardcodeado y se eliminó.)
 */
export const initialsOf = (n) =>
  ((n || '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();

/**
 * Chip de cumplimiento para listas/cards del directorio. Recalcula el resumen
 * en cada render (barato: O(6) por contract) leyendo localStorage en sincrónico.
 */
export function CumplimientoChip({ contract, conMotivo }) {
  const { t } = useTranslation();
  const resumen = resumenDeContract(contract, readComplianceStored(contract?._id));
  const motivos = motivosDe(resumen, t);
  return (
    <div className="min-w-0">
      <EstadoCumplimientoBadge estado={resumen.estado} corto />
      {conMotivo && motivos[0] && (
        <div className="mt-0.5 truncate text-[11px] text-fg-muted" title={motivos[0]}>
          {motivos[0]}
        </div>
      )}
    </div>
  );
}

// ─── Toasts front-only ───────────────────────────────────────────────────────

let toastSeq = 0;

/** Hook de toasts efímeros (auto-desaparecen a los 3400 ms). */
export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((msg) => {
    toastSeq += 1;
    const id = `${Date.now()}_${toastSeq}`;
    setToasts((prev) => [...prev, { id, msg }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 3400);
  }, []);
  return { toasts, push };
}

/**
 * Render de los toasts (fixed bottom-right). Portal a <body> con z-index por
 * sobre el Drawer (z-[9999]): los toasts disparados desde dentro de un drawer
 * deben pintarse encima de él.
 */
export function Toasts({ toasts }) {
  if (!toasts?.length) return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[10000] max-w-sm space-y-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="flex items-start gap-2 rounded-xl border border-border bg-bg px-3 py-2 shadow-elevated"
        >
          <CheckCircle2
            className="mt-0.5 h-4 w-4 shrink-0 text-status-success"
            aria-hidden="true"
          />
          <span className="text-sm text-fg">{toast.msg}</span>
        </div>
      ))}
    </div>,
    document.body,
  );
}
